import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet, Platform, Image } from 'react-native';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '../../store';
import { getCurrentUser, getUserProfile } from '@cubemeet/core/store/slices/authSlice';
import { getMyTravels, getTravelDetail } from '@cubemeet/core/store/slices/travelsSlice';
import { useTravelI18n } from './useTravelI18n';
import { ModuleHeader, type BottomTabItem } from '@cubemeet/ui';
import TopLeftMenu from '../../components/TopLeftMenu';
import { SafeAreaView } from 'react-native-safe-area-context';
import { supabase } from '@cubemeet/core/services/supabase';
import TravelDetail from './TravelDetail';
import CreateTravel from './CreateTravel';
import InviteFriends from './InviteFriends';
import ExpenseSettings from './ExpenseSettings';
// ItineraryEditor and UploadReceiptWebPage live in the module-travel package/web entry
import { ItineraryEditor } from '@cubemeet/module-travel';
import { TravelCard } from '@cubemeet/ui';
// Use the canonical web upload page under the expense module
import UploadReceiptWebPage from '../expense/UploadReceiptWebPage';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { MODULE_COLORS } from '@cubemeet/core/theme';
import { showSuccess, showError } from '@cubemeet/core';
import AddToItineraryModal from '../../components/AddToItineraryModal';
type TravelItineraryEntry = {
  id: string;
  day_number: number;
  day_date: string | null;
  sort_order: number;
  item_type: string;
  card_id?: string | null;
  card?: {
    card_type?: string | null;
    title?: string | null;
    location?: string | null;
    base_payload?: Record<string, any> | null;
    tags?: unknown;
  } | null;
  note?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  inline_payload?: Record<string, any> | null;
  custom_payload?: Record<string, any> | null;
  creator_id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  parent_entry_id?: string | null;
  child_sort?: number | null;
};

type TravelRecord = {
  id: string;
  name: string;
  location?: string | null;
  background_image_url?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  status?: string | null;
  creator_id?: string | null;
  itinerary_entries?: TravelItineraryEntry[];
  [key: string]: any;
};

const SHARE_INTENT_STORAGE_KEY = 'cm-share-intent';

type TravelShareIntentSource = 'share-target' | 'manual';

type TravelShareIntent = {
  id: string;
  title?: string;
  text?: string;
  url?: string;
  travelId?: string | null;
  createdAt: number;
  source?: TravelShareIntentSource;
};

const generateShareIntentId = () => `share-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

const isPlainObject = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const trimOrUndefined = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const sanitizeTimeValue = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const str = String(value);
  if (str.includes(':')) {
    return str.slice(0, 5);
  }
  return str;
};

const clampText = (input: string | undefined, maxLength: number): string | undefined => {
  if (!input) return undefined;
  if (input.length <= maxLength) return input;
  return `${input.slice(0, Math.max(0, maxLength - 1))}…`;
};

const normalizeShareIntent = (value: unknown): TravelShareIntent | null => {
  if (!isPlainObject(value)) return null;
  const rawId = trimOrUndefined((value as any).id);
  const rawTitle = trimOrUndefined((value as any).title);
  const rawText = trimOrUndefined((value as any).text);
  const rawUrl = trimOrUndefined((value as any).url);
  const rawTravelId = trimOrUndefined((value as any).travelId);
  const rawSource = trimOrUndefined((value as any).source);
  const rawCreatedAt = (value as any)?.createdAt;

  if (!rawTitle && !rawText && !rawUrl && !rawTravelId) {
    return null;
  }

  const createdAt = typeof rawCreatedAt === 'number' && Number.isFinite(rawCreatedAt)
    ? rawCreatedAt
    : Date.now();

  const source: TravelShareIntent['source'] | undefined = rawSource === 'share-target' || rawSource === 'manual'
    ? (rawSource as TravelShareIntent['source'])
    : undefined;

  return {
    id: rawId ?? generateShareIntentId(),
    title: rawTitle,
    text: rawText,
    url: rawUrl,
    travelId: rawTravelId ?? null,
    createdAt,
    source,
  };
};

const composeShareClipboardText = (intent: TravelShareIntent): string => {
  const parts: string[] = [];
  if (intent.title) parts.push(intent.title);
  if (intent.text) parts.push(intent.text);
  if (intent.url) parts.push(intent.url);
  return parts.join('\n');
};

const mapCardTypeToEditorType = (cardType?: string | null): string => {
  const type = (cardType || '').toLowerCase();
  switch (type) {
    case 'restaurant':
      return 'meal';
    case 'lodging':
      return 'lodging';
    case 'activity':
      return 'activity';
    case 'shopping':
      return 'activity';
    case 'transport':
      return 'transport';
    case 'poi':
    default:
      return type || 'poi';
  }
};

const deriveEditorType = (entry: TravelItineraryEntry): string => {
  if (entry.item_type === 'card') {
    return mapCardTypeToEditorType(entry.card?.card_type);
  }
  if (entry.item_type === 'note') {
    return 'activity';
  }
  if (entry.item_type === 'custom') {
    const customType = trimOrUndefined((entry.custom_payload as any)?.type);
    return customType || 'activity';
  }
  return entry.item_type || 'poi';
};

const buildItineraryFromEntries = (
  entries: TravelItineraryEntry[] | undefined,
  travelName?: string | null,
  travelLocation?: string | null,
  memberProfiles?: Map<string, any> | Record<string, any> | null,
): any | null => {
  if (!entries || entries.length === 0) return null;

  type DayBucket = {
    dayNumber: number;
    dayDate: string | null;
    entries: TravelItineraryEntry[];
  };

  const dayMap = new Map<number, DayBucket>();

  const lookupProfile = (creatorId?: string | null) => {
    if (!creatorId || !memberProfiles) return null;
    if (memberProfiles instanceof Map) {
      return memberProfiles.get(String(creatorId)) || null;
    }
    return (memberProfiles as Record<string, any>)[String(creatorId)] || null;
  };

  entries.forEach((entry) => {
    const dayNumber = typeof entry.day_number === 'number' && entry.day_number > 0 ? entry.day_number : 1;
    if (!dayMap.has(dayNumber)) {
      dayMap.set(dayNumber, {
        dayNumber,
        dayDate: entry.day_date || null,
        entries: [],
      });
    }
    const bucket = dayMap.get(dayNumber)!;
    if (!bucket.dayDate && entry.day_date) {
      bucket.dayDate = entry.day_date;
    }
    bucket.entries.push(entry);
  });

  const createItemFromEntry = (entry: TravelItineraryEntry) => {
    const inlinePayload = isPlainObject(entry.inline_payload) ? entry.inline_payload : undefined;
    const baseCardPayload = isPlainObject(entry.card?.base_payload) ? entry.card?.base_payload : undefined;
    const customPayload = isPlainObject(entry.custom_payload) ? entry.custom_payload : undefined;

    const payloadBase: Record<string, any> = entry.item_type === 'card'
      ? { ...(baseCardPayload || {}), ...(customPayload || {}) }
      : { ...(inlinePayload || {}), ...(customPayload || {}) };

    if (entry.item_type === 'card') {
      if (entry.card?.location && !payloadBase.location) {
        payloadBase.location = entry.card.location;
      }
      if (entry.card_id) {
        payloadBase.cardId = entry.card_id;
      }
    }

    if (entry.start_time && !payloadBase.start_time) {
      payloadBase.start_time = entry.start_time;
    }
    if (entry.end_time && !payloadBase.end_time) {
      payloadBase.end_time = entry.end_time;
    }

    // Prefer explicit custom_payload.title (for external share entries) before other fallbacks
    const externalTitle = trimOrUndefined((customPayload as any)?.title);
    let candidateTitle = trimOrUndefined(
      externalTitle ||
        entry.card?.title ||
        (payloadBase && trimOrUndefined(payloadBase.title)) ||
        (payloadBase && trimOrUndefined(payloadBase.name)) ||
        (payloadBase && trimOrUndefined(payloadBase.placeName)) ||
        (payloadBase && trimOrUndefined(payloadBase.restaurantName)) ||
        (inlinePayload && trimOrUndefined((inlinePayload as any).title)) ||
        (inlinePayload && trimOrUndefined((inlinePayload as any).name)) ||
        (inlinePayload && trimOrUndefined((inlinePayload as any).placeName)) ||
        (inlinePayload && trimOrUndefined((inlinePayload as any).restaurantName)) ||
        entry.note,
    ) || `行程条目`;

    const parentEntryId = entry.parent_entry_id || null;
    const childSort = typeof entry.child_sort === 'number' ? entry.child_sort : null;
    const creatorId = entry.creator_id || null;
    const createdAt = entry.created_at || null;
    const updatedAt = entry.updated_at || createdAt || null;
    const creatorProfile = lookupProfile(creatorId);
    const creatorName = creatorProfile?.display_name || creatorProfile?.nickname || creatorProfile?.name || undefined;

    const payload = Object.keys(payloadBase).length > 0 ? payloadBase : undefined;

    const meta = {
      itineraryEntryId: entry.id,
      cardId: entry.card_id,
      itemType: entry.item_type,
      parentEntryId,
      childSort,
      creatorId,
      createdAt,
      updatedAt,
      creatorProfile,
    };

    const item: Record<string, any> = {
      id: entry.id,
      type: deriveEditorType(entry),
      title: candidateTitle,
      note: trimOrUndefined(entry.note),
      time: sanitizeTimeValue(entry.start_time),
      payload,
      custom_payload: entry.custom_payload || null, // CRITICAL: preserve original custom_payload from database
      card: entry.card || null,
      cardTags: Array.isArray(entry.card?.tags)
        ? (entry.card?.tags as string[]).filter((tag) => typeof tag === 'string' && tag.trim())
        : undefined,
      meta,
      parentEntryId,
      childSort,
      creator_id: creatorId,
      creatorId,
      created_at: createdAt,
      createdAt,
      updated_at: updatedAt,
      updatedAt,
      creator_profile: creatorProfile || null,
      creatorProfile,
      creator_name: creatorName,
      creatorName,
    };

    // If this is an external share (custom_payload.type === 'external') and we fell back to generic title,
    // force using a better display title (host/domain or explicit title) to avoid placeholder rendering.
    if (
      customPayload &&
      (customPayload as any).type === 'external' &&
      (item.title === '行程条目' || item.title === '条目' || !item.title?.trim())
    ) {
      const hostFromWebsite = (() => {
        const website = (customPayload as any).website || (customPayload as any).url;
        if (!website || typeof website !== 'string') return undefined;
        try { return new URL(website).hostname; } catch { return undefined; }
      })();
      const improved = trimOrUndefined((customPayload as any).title) || (hostFromWebsite ? `来自 ${hostFromWebsite}` : '外部分享');
      if (improved) item.title = improved;
      // mark meta to signal non-card external origin so TimelineItem can choose not to treat as empty
      item.meta = { ...item.meta, externalShare: true };
    }

    return item;
  };

  const days = Array.from(dayMap.values())
    .sort((a, b) => a.dayNumber - b.dayNumber)
    .map((bucket) => {
      const childGroups = new Map<string, TravelItineraryEntry[]>();
      bucket.entries.forEach((entry) => {
        if (entry.parent_entry_id) {
          const key = entry.parent_entry_id;
          const list = childGroups.get(key) || [];
          list.push(entry);
          childGroups.set(key, list);
        }
      });

      const parentEntries = bucket.entries.filter((entry) => !entry.parent_entry_id);
      const parentIdSet = new Set(parentEntries.map((entry) => entry.id));

      const items = parentEntries
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        .map((entry) => {
          const item = createItemFromEntry(entry);
          const children = childGroups.get(entry.id);
          if (children && children.length) {
            const sortedChildren = [...children].sort((a, b) => {
              const aKey = typeof a.child_sort === 'number' ? a.child_sort : (a.sort_order ?? 0);
              const bKey = typeof b.child_sort === 'number' ? b.child_sort : (b.sort_order ?? 0);
              return aKey - bKey;
            });
            item.children = sortedChildren.map((childEntry) => createItemFromEntry(childEntry));
          }
          return item;
        });

      childGroups.forEach((children, parentId) => {
        if (!parentIdSet.has(parentId) && children.length) {
          console.warn('[buildItineraryFromEntries] 子項目缺少對應的父項目', {
            parentId,
            dayNumber: bucket.dayNumber,
            childCount: children.length,
          });
          const fallbackItems = [...children]
            .sort((a, b) => {
              const aKey = typeof a.child_sort === 'number' ? a.child_sort : (a.sort_order ?? 0);
              const bKey = typeof b.child_sort === 'number' ? b.child_sort : (b.sort_order ?? 0);
              return aKey - bKey;
            })
            .map((childEntry) => {
              const orphanItem = createItemFromEntry(childEntry);
              orphanItem.parentEntryId = null;
              if (orphanItem.meta) {
                orphanItem.meta = {
                  ...orphanItem.meta,
                  parentEntryId: null,
                  childSort: orphanItem.childSort,
                  orphanParentId: parentId,
                };
              }
              orphanItem.detachedFromParent = parentId;
              return orphanItem;
            });
          items.push(...fallbackItems);
        }
      });

      return {
        day: bucket.dayNumber,
        date: bucket.dayDate || undefined,
        items,
      };
    });

  return {
    title: travelName || undefined,
    location: travelLocation || undefined,
    days,
  };
};

const TravelModule: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTravelI18n();
  type TravelView = 'list' | 'editor' | 'upload' | 'detail' | 'create' | 'settings';
  const [currentView, setCurrentView] = useState<TravelView>('list');
  const [selectedTravelId, setSelectedTravelId] = useState<string | null>(null);
  const [inviteContext, setInviteContext] = useState<{
    groupId: string;
    groupName?: string | null;
  } | null>(null);
  const [expenseContext, setExpenseContext] = useState<{
    groupId: string;
  } | null>(null);
  const [travelCreator, setTravelCreator] = useState<any>(null);

  const authState = useSelector((state: RootState) => (state as any)?.auth ?? {}) as any;
  const travelState = useSelector((state: RootState) => (state as any)?.travels ?? {}) as any;

  const {
    user,
    profile,
    isAuthenticated,
    loading: authLoading,
    error: authError,
  } = authState;

  const {
    travels = [],
    loading: travelsLoading = false,
    error: travelsError = null,
  } = travelState;

  const currentTravel = travelState?.currentTravel || null;

  const travelList = useMemo<TravelRecord[]>(
    () => (Array.isArray(travels) ? (travels as TravelRecord[]) : []),
    [travels],
  );

  const location = useLocation();
  const navigate = useNavigate();
  const [shareIntent, setShareIntent] = useState<TravelShareIntent | null>(null);
  const [shareBannerDismissed, setShareBannerDismissed] = useState(false);
  const [shareAddModalOpen, setShareAddModalOpen] = useState(false);
  const consumedShareIdRef = useRef<string | null>(null);
  const shareIntentRef = useRef<TravelShareIntent | null>(null);
  const sharePreviewText = useMemo(() => {
    if (!shareIntent) return undefined;
    const primary = shareIntent.text || shareIntent.url || shareIntent.title;
    return clampText(primary, 180);
  }, [shareIntent]);

  useEffect(() => {
    shareIntentRef.current = shareIntent;
    consumedShareIdRef.current = shareIntent ? shareIntent.id : null;
  }, [shareIntent]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!shareIntent) {
      try {
        window.sessionStorage.removeItem(SHARE_INTENT_STORAGE_KEY);
      } catch (err) {
        // ignore
      }
      return;
    }
    try {
      window.sessionStorage.setItem(SHARE_INTENT_STORAGE_KEY, JSON.stringify(shareIntent));
    } catch (err) {
      console.warn('TravelModule: failed to persist share intent', err);
    }
  }, [shareIntent]);

  useEffect(() => {
    if (!shareIntent) return;
    setShareBannerDismissed(false);
  }, [shareIntent?.id]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(location.search || '');
    const shareFlag = params.get('fromShare');
    const shareTitleParam = params.get('shareTitle');
    const shareTextParam = params.get('shareText');
    const shareUrlParam = params.get('shareUrl');
    const shareTravelIdParam = params.get('shareTravelId');

    const hasQueryShare = Boolean(
      shareFlag || shareTitleParam || shareTextParam || shareUrlParam || shareTravelIdParam,
    );

    let parsedIntent: TravelShareIntent | null = null;

    const storedRaw = window.sessionStorage.getItem(SHARE_INTENT_STORAGE_KEY);
    if (storedRaw) {
      try {
        const candidate = normalizeShareIntent(JSON.parse(storedRaw));
        if (candidate) {
          const alreadyConsumed =
            consumedShareIdRef.current !== null &&
            consumedShareIdRef.current === candidate.id &&
            !hasQueryShare;
          if (!alreadyConsumed) {
            parsedIntent = candidate;
          }
        }
      } catch (err) {
        console.warn('TravelModule: failed to parse stored share intent payload', err);
        try {
          window.sessionStorage.removeItem(SHARE_INTENT_STORAGE_KEY);
        } catch (cleanupErr) {
          // ignore cleanup failure
        }
      }
    }

    if (!parsedIntent && hasQueryShare) {
      const synthetic = normalizeShareIntent({
        id: generateShareIntentId(),
        title: shareTitleParam,
        text: shareTextParam,
        url: shareUrlParam,
        travelId: shareTravelIdParam,
        source: shareFlag ? 'share-target' : undefined,
        createdAt: Date.now(),
      });
      if (synthetic) {
        parsedIntent = synthetic;
      }
    }

    const previousIntent = shareIntentRef.current;

    if (parsedIntent) {
      if (shareTravelIdParam && !parsedIntent.travelId) {
        parsedIntent = { ...parsedIntent, travelId: shareTravelIdParam };
      }

      const changed =
        !previousIntent ||
        previousIntent.id !== parsedIntent.id ||
        previousIntent.title !== parsedIntent.title ||
        previousIntent.text !== parsedIntent.text ||
        previousIntent.url !== parsedIntent.url ||
        previousIntent.travelId !== parsedIntent.travelId;

      if (changed) {
        setShareIntent(parsedIntent);
      }

      consumedShareIdRef.current = parsedIntent.id;

      if (parsedIntent.travelId && parsedIntent.travelId !== selectedTravelId) {
        setSelectedTravelId(parsedIntent.travelId);
        setCurrentView('detail');
        try {
          dispatch(getTravelDetail(parsedIntent.travelId));
        } catch (err) {
          console.warn('TravelModule: failed to preload travel detail for share intent', err);
        }
      }
    } else if (shareTravelIdParam && shareTravelIdParam !== selectedTravelId) {
      setSelectedTravelId(shareTravelIdParam);
      setCurrentView('detail');
      try {
        dispatch(getTravelDetail(shareTravelIdParam));
      } catch (err) {
        console.warn('TravelModule: failed to preload travel detail via shareTravelId', err);
      }
    }

    if (!hasQueryShare) {
      return;
    }

    params.delete('fromShare');
    params.delete('shareTitle');
    params.delete('shareText');
    params.delete('shareUrl');
    params.delete('shareTravelId');

    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, dispatch, selectedTravelId]);

  const memberProfilesMap = useMemo(() => {
    const map = new Map<string, any>();
    const membersArr = Array.isArray(currentTravel?.members) ? currentTravel.members : [];
    membersArr.forEach((member: any) => {
      const userId = member?.user_id || member?.userId || member?.id;
      if (!userId) return;
      const profile = member?.profile || member?.user_profile || null;
      const entry = profile
        ? {
            id: profile.id || String(userId),
            display_name: profile.display_name ?? profile.nickname ?? profile.name ?? member?.display_name ?? null,
            avatar_url: profile.avatar_url ?? profile.avatarUrl ?? member?.avatar_url ?? member?.avatarUrl ?? null,
          }
        : {
            id: String(userId),
            display_name: member?.display_name ?? member?.nickname ?? member?.name ?? null,
            avatar_url: member?.avatar_url ?? member?.avatarUrl ?? null,
          };
      map.set(String(userId), entry);
    });

    if (currentTravel?.creator_id) {
      const creatorId = String(currentTravel.creator_id);
      if (!map.has(creatorId)) {
        if (travelCreator) {
          map.set(creatorId, {
            id: creatorId,
            display_name: travelCreator.display_name ?? travelCreator.nickname ?? travelCreator.name ?? null,
            avatar_url: travelCreator.avatar_url ?? travelCreator.avatarUrl ?? null,
          });
        } else if (currentTravel.creator) {
          const creatorObj = currentTravel.creator as any;
          map.set(creatorId, {
            id: creatorId,
            display_name: creatorObj.display_name ?? creatorObj.nickname ?? creatorObj.name ?? null,
            avatar_url: creatorObj.avatar_url ?? creatorObj.avatarUrl ?? null,
          });
        }
      }
    }

    return map;
  }, [currentTravel?.members, currentTravel?.creator, currentTravel?.creator_id, travelCreator]);

  const itineraryFromEntries = useMemo(() => {
    if (!currentTravel?.itinerary_entries || currentTravel.itinerary_entries.length === 0) return null;
    return buildItineraryFromEntries(
  currentTravel.itinerary_entries as TravelItineraryEntry[],
      currentTravel.name,
      currentTravel.location,
      memberProfilesMap,
    );
  }, [currentTravel?.id, currentTravel?.itinerary_entries, currentTravel?.name, currentTravel?.location, memberProfilesMap]);

  const expectedDays = useMemo(() => {
    if (!currentTravel?.start_date || !currentTravel?.end_date) return undefined;
    try {
      const start = new Date(currentTravel.start_date);
      const end = new Date(currentTravel.end_date);
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return undefined;
      }
      return Math.max(1, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1);
    } catch {
      return undefined;
    }
  }, [currentTravel?.start_date, currentTravel?.end_date]);

  useEffect(() => {
    dispatch(getCurrentUser());
  }, [dispatch]);

  // 查询行程创建人信息（用于 ShareButton）
  useEffect(() => {
    if (!currentTravel?.creator_id) {
      setTravelCreator(null);
      return;
    }

    const fetchCreator = async () => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('id, display_name, avatar_url, bio')
          .eq('id', currentTravel.creator_id)
          .single();
        
        if (data) {
          setTravelCreator(data);
          console.log('[TravelModule] Creator loaded:', data);
        }
      } catch (e) {
        console.warn('[TravelModule] Failed to fetch creator info:', e);
      }
    };

    fetchCreator();
  }, [currentTravel?.creator_id]);

  // Listen to hash params to allow opening editor via URL
  useEffect(() => {
    const parseHash = () => {
      try {
        if (typeof window === 'undefined') return;
        const h = window.location.hash || '';
        // support patterns like #travel?view=editor&groupId=xxx
        if (h.startsWith('#travel')) {
          const q = h.includes('?') ? h.split('?')[1] : '';
          const params = new URLSearchParams(q);
          const view = params.get('view');
          const gid = params.get('groupId');

          // If there's no explicit view or view === 'list', show the travel list
          if (!view || view === 'list') {
            setSelectedTravelId(null);
            setCurrentView('list');
            return;
          }

          if (view === 'editor' && gid) {
            setSelectedTravelId(gid);
            setCurrentView('editor');
            // proactively fetch travel detail so editor has data on hard refresh
            try {
              // dispatch is stable, safe to use
              // @ts-ignore
              dispatch(getTravelDetail(gid));
            } catch {}
            return;
          }
          // support direct navigation to upload page: #travel?view=upload&groupId=xxx
          if (view === 'upload' && gid) {
            setSelectedTravelId(gid);
            setCurrentView('upload');
            try {
              // ensure detail is loaded
              // @ts-ignore
              dispatch(getTravelDetail(gid));
            } catch {}
            return;
          }
        }
      } catch (e) {
        // ignore
      }
    };

    parseHash();
    window.addEventListener('hashchange', parseHash);
    return () => window.removeEventListener('hashchange', parseHash);
  }, []);

  useEffect(() => {
    if (!user?.id) {
      return;
    }

    dispatch(getMyTravels(user.id));
  }, [dispatch, user?.id]);

  // When opening editor view, ensure travel detail is loaded
  useEffect(() => {
    if (currentView === 'editor' && selectedTravelId) {
      dispatch(getTravelDetail(selectedTravelId));
    }
  }, [dispatch, currentView, selectedTravelId]);

  useEffect(() => {
    if (!user?.id) return;
    if (!profile || profile.id !== user.id) {
      dispatch(getUserProfile(user.id));
    }
  }, [dispatch, profile, user?.id]);

  const travelTitle = t('travelList.title');
  const createTravelTitle = t('createTravel.title');

  const resolvedTravelTitle = travelTitle === 'travelList.title' ? '旅行项目' : travelTitle;
  const resolvedCreateTitle =
    createTravelTitle === 'createTravel.title' ? '创建旅行' : createTravelTitle;

  const [menuOpen, setMenuOpen] = useState(false);

  const handleCreate = useCallback(() => {
    if (shareIntent && !shareIntent.travelId && typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem(SHARE_INTENT_STORAGE_KEY, JSON.stringify(shareIntent));
      } catch (err) {
        console.warn('TravelModule: failed to persist share intent before navigating to create', err);
      }
    }

    // Use router navigation so the CreateTravel page is rendered by the route
    // outside of AppLayout (full-bleed).
    try {
      navigate('/travel/create');
      return;
    } catch {
      // fallback for non-router environments
      setCurrentView('create');
    }
  }, [navigate, shareIntent]);

  const handleMenuToggle = useCallback(() => {
    setMenuOpen((v) => !v);
  }, []);

  const handleMenuClose = useCallback(() => setMenuOpen(false), []);

  const handleMenuSettings = useCallback(() => {
    navigate('/dashboard');
  }, [navigate]);

  const handleMenuProfile = useCallback(() => {
    navigate('/profile');
  }, [navigate]);

  const handleMenuLogout = useCallback(() => {
    // placeholder: you should wire this to real logout flow
    if (typeof window !== 'undefined') window.alert('Logout (placeholder)');
  }, []);

  const handleBackToList = useCallback(() => {
    setCurrentView('list');
    setSelectedTravelId(null);
    setInviteContext(null);
    setExpenseContext(null);
  }, []);

  const handleViewTravel = useCallback((travelId: string) => {
    setSelectedTravelId(travelId);
    setCurrentView('detail');
  }, []);

  const handleOpenEditor = useCallback((travelId: string) => {
    setSelectedTravelId(travelId);
    setCurrentView('editor');
  }, []);

  const handleApplyShareToTravel = useCallback(
    (travelId: string) => {
      if (!travelId) return;
      setShareIntent((prev) => {
        if (!prev) return prev;
        if (prev.travelId === travelId) return prev;
        return { ...prev, travelId };
      });
      setShareBannerDismissed(false);
      setSelectedTravelId(travelId);
      setCurrentView('detail');
      try {
        dispatch(getTravelDetail(travelId));
      } catch (err) {
        console.warn('TravelModule: failed to preload travel detail after selecting share target', err);
      }
    },
    [dispatch],
  );

  const handleShareDismiss = useCallback(() => {
    setShareBannerDismissed(true);
    setShareIntent(null);
  }, []);

  const handleShareCopy = useCallback(async () => {
    const intent = shareIntentRef.current;
    if (!intent) return;
    const text = composeShareClipboardText(intent);
    if (!text) return;

    const fallbackCopy = () => {
      if (typeof window === 'undefined') return;
      window.prompt('复制以下内容以保存分享信息', text);
    };

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        try {
          showSuccess && showSuccess('分享内容已复制');
        } catch (err) {
          if (typeof window !== 'undefined') window.alert('分享内容已复制');
        }
        return;
      }
    } catch (err) {
      console.warn('TravelModule: clipboard write failed', err);
    }

    fallbackCopy();
  }, []);

  const handleShareOpenEditorFromOverlay = useCallback(() => {
    const travelId = selectedTravelId;
    if (!travelId) return;
    setCurrentView('editor');
    setSelectedTravelId(travelId);
  }, [selectedTravelId]);

  const handleOpenShareAddModal = useCallback(() => {
    setShareAddModalOpen(true);
  }, []);

  const handleShareAdded = useCallback((entry: any) => {
    try {
      const gid = entry?.group_id || entry?.groupId || selectedTravelId;
      if (gid) {
        setShareAddModalOpen(false);
        setCurrentView('detail');
        setSelectedTravelId(gid);
        navigate(`/trip/${gid}`);
      }
    } catch (e) {
      setShareAddModalOpen(false);
    }
  }, [navigate, selectedTravelId]);

  const handleShareOpenLink = useCallback(() => {
    const intent = shareIntentRef.current;
    if (!intent?.url) return;
    if (typeof window === 'undefined') return;
    try {
      window.open(intent.url, '_blank', 'noopener');
    } catch (err) {
      console.warn('TravelModule: failed to open shared link', err);
    }
  }, []);

  const handleTravelCardPress = useCallback(
    (travelId: string) => {
      if (shareIntent && !shareIntent.travelId) {
        handleApplyShareToTravel(travelId);
        return;
      }
      handleViewTravel(travelId);
    },
    [handleApplyShareToTravel, handleViewTravel, shareIntent],
  );

  const handleOpenInviteView = useCallback(
    (groupName?: string | null) => {
      if (!selectedTravelId) return;
      setInviteContext({
        groupId: selectedTravelId,
        groupName,
      });
      setCurrentView('invite');
    },
    [selectedTravelId],
  );

  const handleOpenExpenseSettings = useCallback(() => {
    if (!selectedTravelId) return;
    setExpenseContext({ groupId: selectedTravelId });
    setCurrentView('expense-settings');
    try {
      dispatch(getTravelDetail(selectedTravelId));
    } catch (error) {
      console.error('Failed to refresh travel detail before opening expense settings', error);
    }
  }, [dispatch, selectedTravelId]);

  const handleBackFromExpense = useCallback(() => {
    setCurrentView('detail');
    setExpenseContext(null);
  }, []);

  const handleExpenseSaved = useCallback(async () => {
    if (!selectedTravelId) {
      setExpenseContext(null);
      setCurrentView('detail');
      return;
    }
    try {
      await dispatch(getTravelDetail(selectedTravelId)).unwrap();
    } catch (error) {
      console.error('Failed to refresh travel detail after saving expense settings', error);
    }
    setExpenseContext(null);
    setCurrentView('detail');
  }, [dispatch, selectedTravelId]);

  const handleBackFromInvite = useCallback(() => {
    setCurrentView('detail');
    setInviteContext(null);
  }, []);

  const handleDiscover = useCallback(() => {
    // Discover feature is currently disabled on web; no-op.
  }, []);

  // Bottom tab bar items and navigation mapping
  const bottomItems: BottomTabItem[] = useMemo(
    () => ([
      { key: 'settings', label: 'Settings', icon: 'settings' },
      { key: 'notifications', label: 'Notifications', icon: 'notifications', badge: 2 },
      { key: 'hub', label: '', icon: 'apps' },
      { key: 'profile', label: 'Profile', icon: 'person' },
      { key: 'friends', label: 'Friends', icon: 'people' },
    ]),
    [],
  );

  const onTabPress = useCallback((index: number, key: string) => {
    if (typeof window === 'undefined') return;
    const map: Record<string, string> = {
      settings: 'dashboard',
      notifications: 'notifications',
      hub: 'dashboard',
      profile: 'profile',
      friends: 'friends',
    };
    const target = map[key] || 'dashboard';
    window.location.hash = target;
  }, []);

  const onCenterPress = useCallback(() => {
    if (typeof window !== 'undefined') window.location.hash = 'dashboard';
  }, []);

  const navigateToAuth = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.location.hash = 'auth';
    }
  }, []);

  if (authLoading && !user) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.fullscreenState}>
          <ActivityIndicator size="large" color={MODULE_COLORS.travel} />
          <Text style={styles.centeredText}>正在加载登录状态…</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!isAuthenticated || !user) {
    // 如果在瀏覽器環境，直接導向登入頁，帶上 referer 以便登入後回來
    if (typeof window !== 'undefined') {
      try {
        const referer = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/auth?referer=${referer}`;
      } catch (e) {
        // fallback: 使用 hash 導向
        try { window.location.hash = 'auth'; } catch (__) {}
      }
      return null;
    }

    return (
      <SafeAreaView style={styles.safeArea}>
        <View style={styles.fullscreenState}>
          <ActivityIndicator size="large" color={MODULE_COLORS.travel} />
          <Text style={styles.centeredText}>正在跳轉到登录页面…</Text>
        </View>
      </SafeAreaView>
    );
  }

  // If this component is used as part of the AppLayout routing, actual
  // /travel/create path will render CreateTravel separately. Keep a fallback
  // for non-router usage.
  if (currentView === 'create') {
    if (typeof window !== 'undefined' && window.location.pathname === '/travel/create') {
      return <CreateTravel onBack={handleBackToList} />;
    }
    return null;
  }

  if (currentView === 'invite' && inviteContext) {
    return (
      <InviteFriends
        groupId={inviteContext.groupId}
        groupName={inviteContext.groupName}
        userId={user?.id ?? ''}
        onBack={handleBackFromInvite}
      />
    );
  }

  if (currentView === 'expense-settings' && expenseContext) {
    const detailTravel = currentTravel && currentTravel.id === expenseContext.groupId ? currentTravel : null;

    return (
      <ExpenseSettings
        groupId={expenseContext.groupId}
        travel={detailTravel}
        userId={user?.id ?? ''}
        onBack={handleBackFromExpense}
        onSaved={handleExpenseSaved}
      />
    );
  }

  // 显示旅行详情视图
  if (currentView === 'detail' && selectedTravelId) {
    const shouldShowShareOverlay =
      shareIntent && shareIntent.travelId === selectedTravelId;

    return (
      <View style={styles.detailContainer}>
        <TravelDetail
          groupId={selectedTravelId}
          onBack={handleBackToList}
          onOpenInvite={handleOpenInviteView}
          onOpenExpenseSettings={handleOpenExpenseSettings}
        />
        {shouldShowShareOverlay ? (
          <View style={styles.shareOverlayWrapper}>
            <View style={styles.shareOverlayBackdrop} />
            <View style={styles.shareOverlayCard}>
              <Text style={styles.shareOverlayTitle}>收到新的分享内容</Text>
              {shareIntent?.title ? (
                <Text style={styles.shareOverlaySubtitle}>{shareIntent.title}</Text>
              ) : null}
              {sharePreviewText ? (
                <Text style={styles.shareOverlayPreview} numberOfLines={4}>
                  {sharePreviewText}
                </Text>
              ) : null}
              {shareIntent?.url ? (
                <TouchableOpacity
                  style={styles.shareOverlayLink}
                  onPress={handleShareOpenLink}
                  activeOpacity={0.8}
                >
                  <Text style={styles.shareOverlayLinkText}>{shareIntent.url}</Text>
                </TouchableOpacity>
              ) : null}
              <View style={styles.shareOverlayActions}>
                <TouchableOpacity
                  style={styles.shareOverlayPrimary}
                  onPress={handleShareOpenEditorFromOverlay}
                  activeOpacity={0.9}
                >
                  <Text style={styles.shareOverlayPrimaryText}>打开行程编辑器</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.shareOverlaySecondary}
                  onPress={handleOpenShareAddModal}
                  activeOpacity={0.85}
                >
                  <Text style={styles.shareOverlaySecondaryText}>添加到行程</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.shareOverlaySecondary}
                  onPress={handleShareCopy}
                  activeOpacity={0.85}
                >
                  <Text style={styles.shareOverlaySecondaryText}>复制内容</Text>
                </TouchableOpacity>
              </View>
              <TouchableOpacity
                style={styles.shareOverlayDismiss}
                onPress={handleShareDismiss}
                activeOpacity={0.75}
              >
                <Text style={styles.shareOverlayDismissText}>完成</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null}

        {/* 分享添加模态（在详情视图内也可直接添加）*/}
        <AddToItineraryModal
          visible={Boolean(shareAddModalOpen && shareIntent)}
          onClose={() => setShareAddModalOpen(false)}
          cardSnapshot={null}
          onAdded={handleShareAdded}
          externalShare={shareIntent ? { title: shareIntent.title, text: shareIntent.text, url: shareIntent.url, source: shareIntent.source || 'manual' } : null}
        />
      </View>
    );
  }

  // 显示行程编辑器（完整页面）
  if (currentView === 'editor' && selectedTravelId) {
    const backToDetail = () => {
      setCurrentView('detail');
      // Reset hash so subsequent clicks that set the same editor hash will trigger hashchange
      if (typeof window !== 'undefined') {
        window.location.hash = '#travel';
      }
    };

    const handleEditPress = () => {
      // no-op; edit mode is handled inside the editor
    };
    const persistItinerary = async (payload: { normalized: any; text: string }) => {
      const groupId = selectedTravelId;
      if (!groupId) return;

      try {
        const userId = (authState?.user && authState.user.id) ? authState.user.id : null;
        const isCreator = currentTravel && userId ? currentTravel.creator_id === userId : false;
        const isMember = currentTravel && userId && Array.isArray(currentTravel.members)
          ? currentTravel.members.some((m: any) => {
              const mId = m.user_id || m.userId || m.id;
              return String(mId) === String(userId);
            })
          : false;

        if (!isCreator && !isMember) {
          // TODO: 非创建者且非成员保存草稿的逻辑需要后续实现
            try { showError && showError('您没有权限编辑此行程'); } catch (e) { if (typeof window !== 'undefined') window.alert('您没有权限编辑此行程'); }
          return;
        }

        // 1. 获取数据库中现有的所有 entries
        const { data: existingEntries, error: fetchErr } = await supabase
          .from('group_itinerary_entries')
          .select('id')
          .eq('group_id', groupId);

        if (fetchErr) {
          console.error('获取现有条目失败:', fetchErr);
          throw new Error('获取现有条目失败');
        }

        const existingIds = new Set((existingEntries || []).map((e: any) => e.id));

        // 2. 将编辑器格式转换为 entries，区分新增和更新
        const itinerary = payload.normalized || { days: [] };
        const toUpdate: any[] = [];
        const editorIds = new Set<string>();
        const newParentEntries: Array<{ data: any; clientTempId: string }> = [];
        const newChildEntries: Array<{
          data: any;
          clientTempId: string;
          parentExistingId: string | null;
          parentClientTempId: string | null;
        }> = [];
        const pendingChildUpdates: Array<{
          id: string;
          data: any;
          parentExistingId: string | null;
          parentClientTempId: string | null;
        }> = [];

        const generateTempId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
        
        (itinerary.days || []).forEach((day: any, dayIndex: number) => {
          const dayNumber = day.day || dayIndex + 1;
          const dayDate = day.date || null;

          const pushEntryForItem = (
            item: any,
            sortOrder: number,
            parentEntryId: string | null = null,
            childSort: number | null = null,
            options: { isChild?: boolean; parentClientTempId?: string | null } = {},
          ) => {
            if (!item) {
              return { entryId: null, clientTempId: generateTempId() };
            }

            const cardId = item.meta?.cardId || item.payload?.cardId || null;
            const inferredItemType = item.meta?.itemType || item.itemType || item.item_type;
            let itemType = inferredItemType || 'custom';
            if (!itemType) {
              if (cardId) itemType = 'card';
              else if (item.type === 'transport') itemType = 'transport';
              else if (item.type === 'note') itemType = 'note';
              else itemType = 'custom';
            }

            const entryId = item.meta?.itineraryEntryId || null;
            const clientTempId = String(
              entryId || item.meta?.clientTempId || item.id || item.meta?.id || generateTempId(),
            );

            const entryData: any = {
              group_id: groupId,
              day_number: dayNumber,
              day_date: dayDate,
              sort_order: sortOrder,
              item_type: itemType,
              card_id: cardId || null,
              note: item.note || null,
              start_time: item.time || null,
              end_time: null,
              inline_payload: {},
              custom_payload: {},
              parent_entry_id: parentEntryId,
              child_sort: typeof childSort === 'number' ? childSort : null,
            };

            // Prefer explicit payload, then custom_payload, then inline_payload
            let payloadObject = item && typeof item === 'object' ? (item.payload || item.custom_payload || item.inline_payload || {}) : {};
            if (payloadObject && typeof payloadObject !== 'object') payloadObject = {};

            if (!cardId) {
              entryData.inline_payload = {
                title: item.title || '',
                type: item.type || itemType || 'custom',
                ...payloadObject,
              };
            }

            if (payloadObject && Object.keys(payloadObject).length > 0) {
              // Ensure custom_payload includes type information for proper card type detection
              entryData.custom_payload = {
                type: item.type || itemType || 'custom',
                ...payloadObject,
              };
            }

            if (entryId && existingIds.has(entryId)) {
              const shouldDeferChildUpdate = options?.isChild && !parentEntryId && options.parentClientTempId;
              if (shouldDeferChildUpdate) {
                pendingChildUpdates.push({
                  id: entryId,
                  data: entryData,
                  parentExistingId: parentEntryId,
                  parentClientTempId: options.parentClientTempId || null,
                });
              } else {
                toUpdate.push({ id: entryId, ...entryData });
              }
              editorIds.add(entryId);
              return { entryId, clientTempId };
            }

            const record = {
              data: entryData,
              clientTempId,
              parentExistingId: parentEntryId,
              parentClientTempId: options?.parentClientTempId || null,
            };

            if (options?.isChild) {
              newChildEntries.push(record);
            } else {
              newParentEntries.push(record);
            }

            return { entryId: null, clientTempId };
          };

          (day.items || []).forEach((item: any, itemIndex: number) => {
            const parentResult = pushEntryForItem(item, itemIndex, null, null, { isChild: false });
            const parentEntryId = parentResult.entryId;
            const parentClientTempId = parentResult.clientTempId;

            if (Array.isArray(item?.children) && item.children.length > 0) {
              item.children.forEach((child: any, childIndex: number) => {
                const childSort = typeof child?.childSort === 'number'
                  ? child.childSort
                  : (typeof child?.meta?.childSort === 'number' ? child.meta.childSort : childIndex);
                const resolvedParentId = parentEntryId && existingIds.has(parentEntryId)
                  ? parentEntryId
                  : (item.meta?.itineraryEntryId && existingIds.has(item.meta.itineraryEntryId)
                    ? item.meta.itineraryEntryId
                    : null);

                pushEntryForItem(
                  child,
                  itemIndex,
                  resolvedParentId,
                  childSort,
                  { isChild: true, parentClientTempId: resolvedParentId ? null : parentClientTempId },
                );
              });
            }
          });
        });

        // 3. 删除不在编辑器中的条目
        const toDelete = Array.from(existingIds).filter(id => !editorIds.has(id));
        if (toDelete.length > 0) {
          const { error: deleteErr } = await supabase
            .from('group_itinerary_entries')
            .delete()
            .in('id', toDelete);

          if (deleteErr) {
            console.error('删除条目失败:', deleteErr);
            throw new Error('删除条目失败');
          }
        }

        const parentIdMap = new Map<string, string>();

        // 4. 插入新的父级条目
        // Parallelize parent inserts to reduce wall-clock latency. We still
        // need to record returned ids and backfill child records.
        if (newParentEntries.length > 0) {
          console.debug('[persistItinerary] 準備插入父項目 (並行)', { count: newParentEntries.length, entries: newParentEntries.map((item) => ({ tempId: item.clientTempId })) });
          try {
            const insertPromises = newParentEntries.map(async (parentEntry) => {
              const { data: insertedParent, error: insertParentErr } = await supabase
                .from('group_itinerary_entries')
                .insert(parentEntry.data)
                .select('id')
                .single();

              if (insertParentErr) {
                console.error('插入父条目失败:', insertParentErr, parentEntry);
                throw new Error('插入父条目失败: ' + insertParentErr.message);
              }

              if (insertedParent?.id && parentEntry.clientTempId) {
                parentIdMap.set(parentEntry.clientTempId, insertedParent.id);
                editorIds.add(insertedParent.id);
                existingIds.add(insertedParent.id);

                console.debug('[persistItinerary] 父項目插入完成，更新映射', {
                  clientTempId: parentEntry.clientTempId,
                  newId: insertedParent.id,
                });

                newChildEntries.forEach((childRecord) => {
                  if (!childRecord.parentExistingId && childRecord.parentClientTempId === parentEntry.clientTempId) {
                    childRecord.parentExistingId = insertedParent.id;
                    console.debug('[persistItinerary] 回填子項目的父ID', {
                      childTempId: childRecord.clientTempId,
                      parentTempId: parentEntry.clientTempId,
                      resolvedParentId: insertedParent.id,
                    });
                  }
                });
              }
            });

            await Promise.all(insertPromises);
          } catch (err) {
            console.error('[persistItinerary] 父項目批量插入失敗', err);
            throw err;
          }
        }

        // 5. 插入新的子级条目，并在插入前补齐父级 ID
        if (newChildEntries.length > 0) {
          const preparedChildPayloads: any[] = [];

          newChildEntries.forEach((entry) => {
            const { data, parentExistingId, parentClientTempId } = entry;
            const resolvedParentId = parentExistingId || (parentClientTempId ? parentIdMap.get(parentClientTempId) : null);

            if (!resolvedParentId) {
              console.warn('[persistItinerary] 跳過子項目插入，缺少父項目 ID', { entry });
              return;
            }

            preparedChildPayloads.push({
              ...data,
              parent_entry_id: resolvedParentId,
            });
          });

          if (preparedChildPayloads.length > 0) {
            console.debug('[persistItinerary] 準備插入子項目', preparedChildPayloads);
            const { error: insertChildErr } = await supabase
              .from('group_itinerary_entries')
              .insert(preparedChildPayloads);

            if (insertChildErr) {
              console.error('插入子条目失败:', insertChildErr);
              throw new Error('插入子条目失败: ' + insertChildErr.message);
            }
          }
        }

        if (pendingChildUpdates.length > 0) {
          pendingChildUpdates.forEach((pending) => {
            const resolvedParentId = pending.parentExistingId || (pending.parentClientTempId ? parentIdMap.get(pending.parentClientTempId) : null);
            if (!resolvedParentId) {
              console.warn('[persistItinerary] 延遲更新子項目失敗，缺少父項目 ID', { pending });
              return;
            }
            toUpdate.push({ id: pending.id, ...pending.data, parent_entry_id: resolvedParentId });
          });
        }

  // 6. 批量（并行）更新已存在的条目以减少总耗时
        if (toUpdate.length > 0) {
          try {
            await Promise.all(
              toUpdate.map(async (entry) => {
                const { id, ...updateData } = entry;
                const { error: updateErr } = await supabase
                  .from('group_itinerary_entries')
                  .update(updateData)
                  .eq('id', id);

                if (updateErr) {
                  console.error('更新条目失败:', updateErr, entry);
                  throw new Error('更新条目失败: ' + updateErr.message);
                }
              }),
            );
          } catch (err) {
            console.error('[persistItinerary] 批量更新失败', err);
            throw err;
          }
        }

  // 7. 更新 groups 表的 updated_at
        const { error: updateGroupErr } = await supabase
          .from('groups')
          .update({ updated_at: new Date().toISOString() })
          .eq('id', groupId);

        if (updateGroupErr) {
          console.warn('更新时间戳失败:', updateGroupErr);
        }

  // 8. 刷新详情
        // 7. 刷新详情
        await dispatch(getTravelDetail(groupId)).unwrap();

        try {
          showSuccess && showSuccess('行程已保存');
        } catch (e) {
          if (typeof window !== 'undefined') window.alert('行程已保存');
        }

        // Note: Do NOT automatically navigate away from the editor here.
        // Navigation was previously performed unconditionally which forced the
        // user back to the list/detail view after save. Keep the editor on-screen
        // so the user can continue editing; callers (or higher-level UI) may
        // choose to navigate if desired.
        
      } catch (e: any) {
        console.error('保存行程失败', e);
        try { const msg = e?.message || '保存失败：请检查网络或环境配置（Supabase）'; showError && showError(msg); } catch (err) { if (typeof window !== 'undefined') { const msg = e?.message || '保存失败：请检查网络或环境配置（Supabase）'; window.alert(msg); } }
      }
    };

    // If reloaded directly into editor, wait for detail to load
    if (!currentTravel || currentTravel.id !== selectedTravelId) {
      return (
        <View style={styles.centeredState}>
          <ActivityIndicator size="small" color={MODULE_COLORS.travel} />
          <Text style={styles.centeredText}>正在加载行程详情…</Text>
        </View>
      );
    }

    return (
      <ItineraryEditor
        key={selectedTravelId || 'editor'}
        initialItinerary={itineraryFromEntries || currentTravel?.itinerary_json}
        initialFormData={currentTravel}
        expectedDays={expectedDays}
        onSave={persistItinerary}
        onBack={backToDetail}
        onEdit={handleEditPress}
        showLocationBadge={false}
        supabaseClient={supabase}
        contentType="trip"
        contentId={selectedTravelId}
        creator={travelCreator}
        showShareButton={true}
      />
    );
  }

  // 显示上传收据页面（Web 独立页面）
  if (currentView === 'upload' && selectedTravelId) {
    const backToDetail = () => {
      setCurrentView('detail');
      if (typeof window !== 'undefined') window.location.hash = '#travel';
    };

    const detailTravel = currentTravel && currentTravel.id === selectedTravelId ? currentTravel : null;

    return (
      <UploadReceiptWebPage groupId={selectedTravelId} onClose={backToDetail} />
    );
  }

  // 显示旅行列表视图
  return (
    <View style={styles.container}>
      <View style={styles.mainContainer}>
        {/* debug overlays removed - restored normal web layout */}
        {/* Header */}
        <View style={styles.header}>
          <ModuleHeader
            moduleType="travel"
            title={resolvedTravelTitle}
            subtitle={`欢迎回来，${profile?.display_name || 'Cube Meet'}`}
            showMiniCube
            topInset={0}
            showCreateButton={true}
            onCreatePress={handleCreate}
            onMenuPress={handleMenuToggle}
          />
        </View>

        {/* Top-left menu (absolute) */}
        {menuOpen ? (
          <TopLeftMenu
            onClose={handleMenuClose}
            onSettings={handleMenuSettings}
            onProfile={handleMenuProfile}
            onLogout={handleMenuLogout}
          />
        ) : null}

        {/* debug overlays removed - restored normal web layout */}

        {/* Scrollable content */}
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          showsHorizontalScrollIndicator={false}
        >
          <View style={styles.contentBody}>
            <View style={styles.contentInner}>
              <View style={styles.form}>
                {travelsError ? <Text style={styles.errorBanner}>{travelsError}</Text> : null}

                {shareIntent && !shareIntent.travelId && !shareBannerDismissed ? (
                  <View style={styles.shareBanner}>
                    <Text style={styles.shareBannerTitle}>检测到新的分享内容</Text>
                    {shareIntent.title ? (
                      <Text style={styles.shareBannerSubtitle}>{shareIntent.title}</Text>
                    ) : null}
                    {sharePreviewText ? (
                      <Text style={styles.shareBannerPreview} numberOfLines={3}>
                        {sharePreviewText}
                      </Text>
                    ) : null}
                    <View style={styles.shareBannerActions}>
                      <TouchableOpacity
                        style={styles.shareBannerPrimary}
                        onPress={() => setShareAddModalOpen(true)}
                        activeOpacity={0.85}
                      >
                        <Text style={styles.shareBannerPrimaryText}>添加到行程</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.shareBannerPrimary}
                        onPress={handleShareCopy}
                        activeOpacity={0.85}
                      >
                        <Text style={styles.shareBannerPrimaryText}>复制分享内容</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.shareBannerSecondary}
                        onPress={handleShareDismiss}
                        activeOpacity={0.75}
                      >
                        <Text style={styles.shareBannerSecondaryText}>稍后处理</Text>
                      </TouchableOpacity>
                    </View>
                    <Text style={styles.shareBannerHint}>
                      点击下方任意行程卡片即可接收分享内容，或使用右上角按钮创建新行程。
                    </Text>
                  </View>
                ) : null}

                {travelsLoading && travelList.length === 0 ? (
                  <View style={styles.centeredState}>
                    <ActivityIndicator size="small" color={MODULE_COLORS.travel} />
                    <Text style={styles.centeredText}>正在加载旅行项目…</Text>
                  </View>
                ) : travelList.length === 0 ? (
                  <View style={styles.emptyState}>
                    <Text style={styles.emptyTitle}>还没有旅行项目</Text>
                    <Text style={styles.emptySubtitle}>
                      点击右上角按钮即可创建你的第一个旅行项目。
                    </Text>
                  </View>
                ) : (
                  <View style={styles.cardList}>
                    {travelList.map((travel) => (
                      <View key={travel.id} style={styles.cardWrapper}>
                        <TravelCard
                          travel={travel as any}
                          t={t}
                          descriptionText={null}
                          onPress={() => handleTravelCardPress(travel.id)}
                        />
                      </View>
                    ))}
                  </View>
                )}
              </View>
            </View>
          </View>
        </ScrollView>
      </View>

      {/* Discover floating button removed for web */}

      {/* 列表视图也挂载添加模态，用户可直接选择行程 */}
      <AddToItineraryModal
        visible={Boolean(shareAddModalOpen && shareIntent && !selectedTravelId)}
        onClose={() => setShareAddModalOpen(false)}
        cardSnapshot={null}
        onAdded={handleShareAdded}
        externalShare={shareIntent ? { title: shareIntent.title, text: shareIntent.text, url: shareIntent.url, source: shareIntent.source || 'manual' } : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F4F6',
  },
  safeArea: { flex: 1, backgroundColor: '#F3F4F6' },
  mainContainer: {
    flex: 1,
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    marginBottom: 20,
    overflow: 'hidden',
    ...Platform.select({
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
        elevation: 3,
      },
      web: {
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }
    }),
  },
  header: {
    // backgroundColor: '#FFFFFF', // Removed to let mainContainer background show
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 0, // Removed padding as mainContainer handles width
    paddingVertical: 0,
    paddingBottom: 100,
  },
  contentBody: {
    flexGrow: 1,
  },
  cardList: {
    marginTop: 8,
    paddingHorizontal: 16, // Add padding inside the card
  },
  cardWrapper: {
    marginBottom: 20,
  },
  contentInner: {
    flex: 1,
    width: '100%',
    alignItems: 'center',
  },
  form: {
    width: '100%',
    // maxWidth: 600, // Handled by mainContainer
    alignSelf: 'center',
  },
  centeredState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  centeredText: {
    marginTop: 12,
    fontSize: 14,
    color: '#4B5563',
  },
  emptyState: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    backgroundColor: '#F9FAFB',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#111827',
  },
  emptySubtitle: {
    marginTop: 12,
    fontSize: 15,
    color: '#4B5563',
    textAlign: 'center',
    lineHeight: 22,
  },
  errorBanner: {
    marginBottom: 16,
    backgroundColor: '#FEE2E2',
    color: '#B91C1C',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 12,
    fontSize: 13,
  },
  fullscreenState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
  },
  
  primaryCtaButton: {
    marginTop: 24,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 999,
    backgroundColor: MODULE_COLORS.travel,
  },
  primaryCtaButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '700',
  },
  errorText: {
    marginTop: 16,
    fontSize: 13,
    color: '#B91C1C',
  },
  discoverFab: {
    position: 'absolute',
    right: 20,
    bottom: 112,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: MODULE_COLORS.travel,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        boxShadow: '0px 8px 12px rgba(0,0,0,0.2)',
        zIndex: 999,
      },
      default: {
        shadowColor: MODULE_COLORS.travel,
        shadowOpacity: 0.35,
        shadowOffset: { width: 0, height: 8 },
        shadowRadius: 12,
        elevation: 10,
        zIndex: 999,
      },
    }),
  },
  detailContainer: {
    flex: 1,
    position: 'relative',
    backgroundColor: '#F3F4F6',
  },
  shareBanner: {
    marginBottom: 16,
    padding: 16,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#A7F3D0',
    backgroundColor: '#ECFDF5',
  },
  shareBannerTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#065F46',
  },
  shareBannerSubtitle: {
    marginTop: 6,
    fontSize: 14,
    fontWeight: '600',
    color: '#047857',
  },
  shareBannerPreview: {
    marginTop: 10,
    fontSize: 13,
    lineHeight: 19,
    color: '#065F46',
  },
  shareBannerActions: {
    marginTop: 14,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  shareBannerPrimary: {
    paddingVertical: 8,
    paddingHorizontal: 18,
    borderRadius: 999,
    backgroundColor: '#059669',
    marginRight: 12,
    marginBottom: 8,
  },
  shareBannerPrimaryText: {
    color: '#ECFEFF',
    fontSize: 13,
    fontWeight: '700',
  },
  shareBannerSecondary: {
    paddingVertical: 8,
    paddingHorizontal: 18,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#34D399',
    marginBottom: 8,
  },
  shareBannerSecondaryText: {
    color: '#047857',
    fontSize: 13,
    fontWeight: '600',
  },
  shareBannerHint: {
    marginTop: 10,
    fontSize: 12,
    lineHeight: 18,
    color: '#047857',
  },
  shareOverlayWrapper: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
    // pointerEvents should be applied via style to avoid deprecated prop on web
    pointerEvents: 'box-none',
  },
  shareOverlayBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(17, 24, 39, 0.35)',
  },
  shareOverlayCard: {
    width: '100%',
    maxWidth: 460,
    borderRadius: 24,
    backgroundColor: '#FFFFFF',
    paddingVertical: 28,
    paddingHorizontal: 28,
    ...Platform.select({
      web: {
        boxShadow: '0px 10px 32px rgba(15,23,42,0.2)',
      },
      default: {
        shadowColor: '#0F172A',
        shadowOpacity: 0.2,
        shadowOffset: { width: 0, height: 10 },
        shadowRadius: 32,
        elevation: 24,
      },
    }),
    // ensure card can receive pointer events (use style.pointerEvents on web)
    pointerEvents: 'auto',
  },
  shareOverlayTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#111827',
  },
  shareOverlaySubtitle: {
    marginTop: 8,
    fontSize: 15,
    fontWeight: '600',
    color: '#0F766E',
  },
  shareOverlayPreview: {
    marginTop: 12,
    fontSize: 14,
    lineHeight: 21,
    color: '#374151',
  },
  shareOverlayLink: {
    marginTop: 14,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: '#F3F4F6',
  },
  shareOverlayLinkText: {
    fontSize: 13,
    fontWeight: '600',
    color: MODULE_COLORS.travel,
  },
  shareOverlayActions: {
    marginTop: 20,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  shareOverlayPrimary: {
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 999,
    backgroundColor: MODULE_COLORS.travel,
    marginRight: 12,
    marginBottom: 10,
  },
  shareOverlayPrimaryText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
  shareOverlaySecondary: {
    paddingVertical: 10,
    paddingHorizontal: 22,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: '#0EA5A4',
    marginBottom: 10,
  },
  shareOverlaySecondaryText: {
    color: '#0F766E',
    fontSize: 14,
    fontWeight: '600',
  },
  shareOverlayDismiss: {
    marginTop: 16,
    alignSelf: 'flex-end',
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  
  shareOverlayDismissText: {
    color: '#4B5563',
    fontSize: 13,
    fontWeight: '600',
  },
  // header buttons are rendered by ItineraryEditor; no local header button styles needed
});

export default TravelModule;